<template>
  <div
    class="featured-details critical-component-clear"
    :style="{
      '--section-spacing': spacing,
      '--section-spacing-l': spacingDesktop
    }"
  >
    <div class="featured-details__grid grid">
      <div
        class="col xs4"
        :class="{
          'l8': !reverse,
          'l5-12': reverse
        }"
      >
        <div class="featured-details__media">
          <button
            v-if="slides.length > 1"
            class="featured-details__arrow"
            :aria-label="$string('accessibility.carousel.previous')"
            type="button"
            @click="$refs.media.go('<')"
          >
            <icon-chevron-left />
          </button>

          <splide
            ref="media"
            :options="carouselOptions"
            @splide:active="handleActiveEvent"
          >
            <splide-slide
              v-for="(slide, index) of slides"
              :key="`featured-details-media-${index}`"
            >
              <video-player
                v-if="slide.video"
                :overlay="slide.video && !slide.videoMuted"
                :src="`${slide.video}#t=0.001`"
                block-class="featured-details"
                :autoplay="slide.videoMuted"
                :preload="videoPreloadValue(slide.videoMuted)"
                loop
                playsinline
                :muted="slide.videoMuted"
                :controls="slide.videoControls"
              />

              <art-direction
                v-else
                :image="slide.image"
                ratio="1:1"
                ratio-desktop="4:3"
                breakpoint-sizes="[400]-[600]-[1000]-[1500]-[2000]"
                crop
                :lazyload="index == 0 && !lazyLoad ? false : true"
              />
            </splide-slide>
          </splide>

          <button
            v-if="slides.length > 1"
            class="featured-details__arrow featured-details__arrow--next"
            :aria-label="$string('accessibility.carousel.next')"
            type="button"
            @click="$refs.media.go('>')"
          >
            <icon-chevron-right />
          </button>

          <teleport
            v-if="slides.length > 1 && isMounted"
            :to="$refs.contentContainer"
            :disabled="isMobile"
          >
            <div
              class="
                featured-details__counter
                text-body-xs-label
                text-body-s-label-desktop
              "
            >
              <span
                class="featured-details__count"
                v-text="counter.active"
              />

              <span v-text="counter.total" />
            </div>
          </teleport>
        </div>
      </div>

      <div
        class="col xs4"
        :class="{
          'l4': !reverse,
          'l1-4': reverse
        }"
      >
        <div
          ref="contentContainer"
          class="featured-details__content-container"
        >
          <splide
            ref="content"
            :options="carouselOptions"
          >
            <splide-slide
              v-for="(slide, index) of slides"
              :key="`featured-details-content-${index}`"
            >
              <div class="featured-details__content">
                <component
                  :is="liquid.sectionIndex === 0 && index === 0 ? 'h1' : 'p'"
                  v-if="slide.title"
                  class="text-heading-2xl text-heading-4xl-desktop"
                >
                  <span v-text="slide.title" />
                </component>

                <p
                  v-if="slide.content"
                  class="featured-details__body text-body-s text-body-m-desktop"
                  v-text="slide.content"
                />

                <btn
                  v-if="slide.button && slide.url"
                  :label="slide.button"
                  :url="slide.url"
                />
              </div>
            </splide-slide>
          </splide>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Featured details (featured-details)
 * -----------------------------------------------------------------------------
 * A carousel of content with a supporting image.
 *
 * @param {Object[]} slides - The list of slides.
 * @param {String} slides[].title - The slide title.
 * @param {String} slides[].content - The slide content.
 * @param {String} slides[].button - The button label.
 * @param {String} slides[].url - The slide link, used for the button.
 * @param {Object} slides[].image - The slide image.
 * @param {Object} slides[].video - The video source.
 * @param {Boolean} reverse - Flips the layout of the carousels.
 * @param {String} spacing - Custom spacing value.
 * @param {String} spacingDesktop - Custom spacing value for desktop.
 * @param {Number} liquid.sectionIndex - Section's 0 based index.
 *
 */
import { mapState } from 'vuex'
import { Splide, SplideSlide } from '@splidejs/vue-splide'

import Btn from '~global/btn/btn'
import ArtDirection from '~global/images/art-direction'
import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'
import VideoPlayer from '~async/video-player/video-player'
import { videoPreloadValue } from '~/helpers/general'

export default {
  name: 'FeaturedDetails',

  components: {
    VideoPlayer,
    Btn,
    ArtDirection,
    IconChevronLeft,
    IconChevronRight,
    Splide,
    SplideSlide,
  },

  props: {
    slides: {
      type: Array,
      default: () => [],
    },

    reverse: {
      type: Boolean,
      default: false,
    },

    lazyLoad: {
      type: Boolean,
      default: true,
    },

    spacing: {
      type: String,
      default: '',
    },

    spacingDesktop: {
      type: String,
      default: '',
    },

    liquid: {
      type: Object,
      default: () => ({
        sectionIndex: 0,
      }),
    },
  },

  data() {
    return {
      activeSlide: 1,
      isMounted: false,
      carouselOptions: {
        type: 'fade',
        rewind: true,
        pagination: false,
        arrows: false,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns the counter values.
     * @returns {Object}
     */
    counter() {
      return {
        active: String(this.activeSlide).padStart(2, 0),
        total: String(this.slides.length).padStart(2, 0),
      }
    },
  },

  mounted() {
    this.isMounted = true

    this.syncCarousels()
  },

  methods: {
    videoPreloadValue,

    /**
     * Syncs the carousels to work together.
     */
    syncCarousels() {
      const media = this.$refs.media
      const content = this.$refs.content

      if (media && content) {
        media.sync(content.splide)
      }
    },

    /**
     * Handles the Splide "active" event.
     * - Updates the local user-friendly active slide count.
     *
     * @param {Object} _ - The Splide instance.
     * @param {Object} payload - The event payload.
     * @param {Number} payload.index - The active slide index.
     */
    handleActiveEvent(_, { index }) {
      this.activeSlide = index + 1
    },
  },
}
</script>

<style lang="scss">
@import '@splidejs/vue-splide/css/core';
@import './featured-details';
</style>
